import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { KanbanCard, KanbanHistorico } from "@/types/kanban";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Clock, ArrowRight, X, CheckCircle, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatMotivosLista, hasMotivos, parseDropReasons } from "@/utils/motivosUtils";

interface CandidateHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidate: KanbanCard | null;
  historico: KanbanHistorico[];
}

export function CandidateHistoryModal({
  open,
  onOpenChange,
  candidate,
  historico,
}: CandidateHistoryModalProps) {
  const getInitials = (name: string): string => {
    const words = name.trim().split(' ').filter(word => word.length > 0);
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  };

  const getMovimentoIcon = (tipo: KanbanHistorico['tipo_movimento']) => {
    switch (tipo) {
      case 'drop':
        return <X className="h-4 w-4 text-red-500" />;
      case 'drop_in_place':
        return <X className="h-4 w-4 text-orange-500" />;
      case 'reativacao':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'progressao':
        return <ArrowRight className="h-4 w-4 text-blue-500" />;
      case 'adicao':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'retorno':
        return <ArrowRight className="h-4 w-4 text-orange-500 rotate-180" />;
      case 'reordenacao':
        return <Clock className="h-4 w-4 text-slate-500" />;
      default:
        return <Clock className="h-4 w-4 text-slate-500" />;
    }
  };

  const getMovimentoLabel = (tipo: KanbanHistorico['tipo_movimento']) => {
    switch (tipo) {
      case 'drop':
        return 'Reprovado';
      case 'drop_in_place':
        return 'Descartado';
      case 'reativacao':
        return 'Reativado';
      case 'progressao':
        return 'Movido';
      case 'adicao':
        return 'Adicionado';
      case 'retorno':
        return 'Retornado';
      case 'reordenacao':
        return 'Reordenado';
      default:
        return tipo;
    }
  };

  const formatObservacoes = (observacoes: string | undefined, tipo: KanbanHistorico['tipo_movimento']): string => {
    if (!observacoes) return '';
    
    // Se for reordenação, ajustar números de posição (0-based para 1-based)
    if (tipo === 'reordenacao') {
      // Procura padrões como "de posição 0 para 1" ou "posição 0 para 1"
      return observacoes.replace(
        /(?:de\s+)?posição\s+(\d+)\s+para\s+(\d+)/gi,
        (match, pos1, pos2) => {
          const pos1Adjusted = parseInt(pos1) + 1;
          const pos2Adjusted = parseInt(pos2) + 1;
          return match.includes('de ') 
            ? `de posição ${pos1Adjusted} para ${pos2Adjusted}`
            : `posição ${pos1Adjusted} para ${pos2Adjusted}`;
        }
      );
    }
    
    return observacoes;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Histórico do Candidato</DialogTitle>
          <DialogDescription>
            Histórico completo de movimentações deste candidato no kanban.
          </DialogDescription>
        </DialogHeader>

        {candidate && (
          <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg mb-4">
            <Avatar className="w-12 h-12">
              <AvatarFallback className="bg-linear-to-r from-blue-100 to-purple-100 text-blue-700">
                {getInitials(candidate.freelancer.nome)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h4 className="font-semibold text-slate-900">
                {candidate.freelancer.nome}
              </h4>
              <p className="text-sm text-slate-500">{candidate.freelancer.email}</p>
            </div>
          </div>
        )}

        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-4">
            {historico.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                Nenhuma movimentação registrada
              </div>
            ) : (
              historico.map((item, index) => (
                <div
                  key={item.id}
                  className="flex gap-3 p-4 bg-white border border-slate-200 rounded-lg"
                >
                  <div className="shrink-0 pt-1">
                    {getMovimentoIcon(item.tipo_movimento)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="secondary" className="text-xs">
                        {getMovimentoLabel(item.tipo_movimento)}
                      </Badge>
                      {item.etapa_origem && (
                        <>
                          <span className="text-xs text-slate-500">
                            {item.etapa_origem.nome}
                          </span>
                          <ArrowRight className="h-3 w-3 text-slate-400" />
                        </>
                      )}
                      <span className="text-xs font-medium text-slate-700">
                        {item.etapa_destino.nome}
                      </span>
                    </div>
                    {hasMotivos(item.motivo_drop) && (
                      <div className="text-sm text-red-600 mb-1">
                        <span className="font-medium">
                          {parseDropReasons(item.motivo_drop).length > 1 ? 'Motivos:' : 'Motivo:'}
                        </span>
                        <div className="mt-1">
                          {formatMotivosLista(item.motivo_drop, "text-red-600")}
                        </div>
                      </div>
                    )}
                    {item.observacoes && (
                      <p className="text-sm text-slate-600 mb-1">
                        {formatObservacoes(item.observacoes, item.tipo_movimento)}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-slate-400">
                        {format(new Date(item.timestamp), "dd/MM/yyyy 'às' HH:mm", {
                          locale: ptBR,
                        })}
                      </p>
                      {item.usuario && (
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <User className="h-3 w-3" />
                          <span>
                            {item.usuario.full_name || item.usuario.username || 'Usuário desconhecido'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

